import { get } from "@/services/axiosUitils";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RecipeMacros {
  calories: number;
  carbs_g: number;
  protein_g: number;
  fat_g: number;
  fiber_g: number;
  net_carbs_g: number;
}

export interface RecipeIngredient {
  item: string;
  quantity: string;
}

export interface RecipeApiItem {
  id: number;
  name: string;
  meal_type: string;
  why_today: string;
  estimated_bbs: number;
  estimated_spike_mg_dl: number;
  estimated_glucose_classification: string;
  macros: RecipeMacros;
  ingredients: RecipeIngredient[];
  instructions: string[];
  prep_time_min: number;
  cook_time_min: number;
  bbs_boosters: string[];
  substitution?: string;
}

export interface RecipesApiResponse {
  success?: {
    code: string;
    message: string;
  };
  date?: string;
  context_note?: string;
  recipes: RecipeApiItem[];
}

export interface CardMetric {
  value: string;
  label: string;
}

export type Sentiment = "action_needed" | "great_work" | "suggestion" | "insight";

export interface CardItem {
  id: string;
  sentiment: Sentiment;
  category: string;
  icon_type: string;
  title: string;
  description: string;
  metric: CardMetric;
}

export interface TodayFocus {
  headline: string;
  description: string;
}

export interface AIInsightsResponse {
  success: {
    code: string;
    message: string;
  };
  date: string;
  today_focus?: TodayFocus;
  cards: CardItem[];
}

// ─── Service ─────────────────────────────────────────────────────────────────

class AIInsightsService {
  private static instance: AIInsightsService;

  private constructor() {}

  public static getInstance(): AIInsightsService {
    if (!AIInsightsService.instance) {
      AIInsightsService.instance = new AIInsightsService();
    }
    return AIInsightsService.instance;
  }

  /**
   * Get AI Insights and Recipes for a specific date
   * @param date - Date string in YYYY-MM-DD format (optional, defaults to today)
   * @param config - Additional axios config (e.g., showNotification: false)
   */
  async getAIInsights(
    date?: string,
    config?: any
  ): Promise<AIInsightsResponse> {
    const url = date
      ? `/food_scanner/coaching/insights/?date=${date}`
      : "/food_scanner/coaching/insights/";
    const response = await get<AIInsightsResponse>(url, config);
    return response;
  }

  /**
   * Get AI Recipe suggestions
   * @param date - Date string in YYYY-MM-DD format (optional)
   * @param config - Additional axios config
   */
  async getRecipes(
    date?: string,
    config?: any
  ): Promise<RecipesApiResponse> {
    const url = date
      ? `/food_scanner/coaching/recipes/?date=${date}`
      : "/food_scanner/coaching/recipes/";
    const response = await get<RecipesApiResponse>(url, config);
    return response;
  }
}

export default AIInsightsService.getInstance();
